"use client";
import {
  ChevronLeft,
  ChevronRight,
  Heart,
  Shield,
  User,
  TrendingUp,
  CalendarIcon,
  ChevronsUpDown,
  Check,
} from "lucide-react";
import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useUserContext } from "@/app/_module/store/contexts/userContext";

import {
  Dialog,
  ResponsiveDialogContent,
  DialogHeader,
  DialogTitle,
} from "@/app/_module/components/ui/dialog";
import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import { Label } from "@/app/_module/components/ui/label";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/app/_module/components/ui/command";
import { CurrencyInput } from "@/app/_module/components/CurrencyInput";
import { Calendar } from "@/app/_module/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/app/_module/components/ui/popover";
import { cn } from "@/app/_module/lib/utils";

import { useCreatePolicy, useGetAgencyCarriers } from "../hooks/queries";
import { useGetLeadHouseholds } from "../hooks/households";
import type { CreatePolicyParams } from "../types/create-policy-type";

import CreateHouseholdMemberModal from "./CreateHouseholdMemberModal";
import { isNativePlatform } from "@/lib/capacitor/platform";

// Helper to get current date in YYYY-MM-DD format
const getCurrentDateString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper to calculate expiry date (1 year from issued date)
const calculateExpiryFromIssued = (issuedDateStr: string): string => {
  if (!issuedDateStr) return "";
  const [year, month, day] = issuedDateStr.split('-').map(Number);
  if (!year || !month || !day) return "";
  const expiryYear = year + 1;
  return `${expiryYear}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

interface CreateLeadPolicyProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (success: boolean) => void;
  leadId: string;
  leadName?: string;
  onStatusUpdate?: (leadId: string, newStatus: string) => Promise<void>;
  newStatus?: string;
}

const CreateLeadPolicy = function ({
  isOpen,
  onOpenChange,
  onSave,
  leadId,
  leadName,
  onStatusUpdate,
  newStatus,
}: CreateLeadPolicyProps) {
  const queryClient = useQueryClient();
  const { user } = useUserContext();
  const { mutateAsync: createPolicy } = useCreatePolicy();
  // Fetch household members when modal is open to prevent unnecessary API calls
  const {
    data: householdsResponse,
    isLoading: isHouseholdsLoading,
    refetch: refetchHouseholds,
  } = useGetLeadHouseholds(leadId, isOpen);
  // Fetch agency carriers for the dropdown - only show carriers from the user's agency
  const agencyId = user?.currentAgency?.agency?.id;
  const { data: agencyCarriers = [], isLoading: isCarriersLoading } =
    useGetAgencyCarriers(agencyId);

  // Extract household members from the response - handle both wrapped and direct formats
  const allHouseholdMembers = Array.isArray(householdsResponse)
    ? householdsResponse
    : householdsResponse?.data || [];

  // Filter out the primary lead (policy holder) to avoid showing them twice
  // They're already shown as "Primary Lead" above the household members section
  // canOpen === false: a member outside this agent's leads. A policy can't be
  // written onto a lead they can't open, so it isn't offered here.
  const householdMembers = allHouseholdMembers.filter(
    (member: any) => member.id !== leadId && member.canOpen !== false
  );

  // Get the householdId from the first member (all members share the same household)
  const householdId =
    allHouseholdMembers.length > 0 ? allHouseholdMembers[0].householdId : null;

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isHouseholdModalOpen, setIsHouseholdModalOpen] = useState(false);
  // Native shell: the carrier popover is replaced by an INLINE list (positioned
  // popovers paint offset from their trigger in the WKWebView — unfixable at
  // the popper level; normal document flow has no positioning to get wrong).
  const [carrierSearch, setCarrierSearch] = useState("");
  const [carrierOpen, setCarrierOpen] = useState(false);

  // Helper to get initial policyStage and status based on newStatus
  const getInitialStageAndStatus = () => {
    if (newStatus === "Sold & Approved") {
      return { policyStage: "sold", status: "active" };
    } else if (newStatus === "Underwriting") {
      return { policyStage: "underwriting", status: "pending" };
    } else if (newStatus === "Lapsed") {
      return { policyStage: "sold", status: "lapsed" };
    } else if (newStatus === "Declined") {
      return { policyStage: "underwriting", status: "declined" };
    }
    return { policyStage: "underwriting", status: "pending" };
  };

  const initialStageAndStatus = getInitialStageAndStatus();

  // Form data - using any type to allow temporary coverageAmount field
  const [formData, setFormData] = useState<any>({
    leadId,
    policyNumber: "",
    carrierName: "",
    productType: "",
    annualPremium: "",
    coverageAmount: "", // This will be mapped to premium field (face value)
    status: initialStageAndStatus.status,
    policyStage: initialStageAndStatus.policyStage,
    premium: 0, // This will be set from coverageAmount
    issuedAt: getCurrentDateString(), // Default to current date
  });
  const [selectedLeadId, setSelectedLeadId] = useState(leadId);

  // Update formData when newStatus changes (when modal opens with a specific status)
  useEffect(() => {
    if (isOpen && newStatus) {
      const { policyStage, status } = getInitialStageAndStatus();
      setFormData((prev: any) => ({
        ...prev,
        policyStage,
        status,
      }));
    }
  }, [isOpen, newStatus]);

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      // Calculate expiryDate (1 year from issuedAt)
      const expiryDate = calculateExpiryFromIssued(formData.issuedAt);

      const policyData: any = {
        ...formData,
        leadId: selectedLeadId,
        premium: parseFloat(formData.coverageAmount as any) || 0,
        annualPremium: parseFloat(formData.annualPremium as any) || 0,
        productSold: formData.productType,
        expiryDate, // Include the calculated renewal date
      };

      await createPolicy(policyData);

      onSave(true);
      onOpenChange(false);
      resetForm();

      if (onStatusUpdate) {
        // Determine status to update based on policy status/stage (same logic as UpdateLeadPolicy)
        let statusToUpdate: string;
        if (formData.status === 'lapsed') {
          statusToUpdate = "Lapsed";
        } else if (formData.status === 'declined') {
          statusToUpdate = "Declined";
        } else if (newStatus) {
          statusToUpdate = newStatus;
        } else if (formData.policyStage === "sold") {
          statusToUpdate = "Sold & Approved";
        } else {
          statusToUpdate = "Underwriting";
        }
        onStatusUpdate(selectedLeadId, statusToUpdate).catch(() => {
          // Status update failed - error handled by parent
        });
      }
    } catch (error) {
      onSave(false);
    } finally {
      setIsSubmitting(false);
    }
  };
  const resetForm = () => {
    setCurrentStep(1);
    setFormData({
      leadId,
      policyNumber: "",
      carrierName: "",
      productType: "",
      annualPremium: "",
      coverageAmount: "",
      status: "pending",
      policyStage: "underwriting",
      premium: 0,
      issuedAt: getCurrentDateString(), // Default to current date
    });
    setSelectedLeadId(leadId);
    setIsSubmitting(false);
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-6">
      {[1, 2, 3].map((step, index) => (
        <div key={step} className="flex items-center">
          <div
            className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
              currentStep >= step
                ? "bg-primary text-primary-foreground"
                : "bg-gray-200 text-gray-500"
            }`}
          >
            {step}
          </div>
          {index < 2 && (
            <div
              className={`w-12 h-0.5 mx-2 ${
                currentStep > step ? "bg-primary" : "bg-gray-200"
              }`}
            />
          )}
        </div>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Who is this policy for?
        </h3>
        <p className="text-sm text-gray-600 mb-6">
          Select the person this policy will cover
        </p>
      </div>

      {/* Primary Lead */}
      <div
        onClick={() => setSelectedLeadId(leadId)}
        role="button"
        tabIndex={0}
        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
          selectedLeadId === leadId
            ? "border-primary bg-primary/10"
            : "border-gray-200 hover:border-gray-300"
        }`}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            setSelectedLeadId(leadId);
          }
        }}
      >
        <div className="flex items-center gap-3">
          <User className="h-8 w-8 text-primary" />
          <div>
            <h4 className="font-medium text-gray-900">
              {leadName || "Primary Lead"}
            </h4>
            <p className="text-sm text-gray-500">Policy holder</p>
          </div>
        </div>
      </div>

      {/* Household Members */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-700">
            Household Members
          </h4>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setIsHouseholdModalOpen(true)}
            className="flex items-center gap-2"
          >
            <User className="h-4 w-4" />
            Multiple Policy Sale
          </Button>
        </div>

        {isHouseholdsLoading ? (
          <div className="p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse" />
              <div>
                <div
                  className="h-4 bg-gray-200 rounded animate-pulse mb-1"
                  style={{ width: "120px" }}
                />
                <div
                  className="h-3 bg-gray-200 rounded animate-pulse"
                  style={{ width: "80px" }}
                />
              </div>
            </div>
          </div>
        ) : householdMembers &&
          Array.isArray(householdMembers) &&
          householdMembers.length > 0 ? (
          <div className={`space-y-3 ${householdMembers.length >= 4 ? "max-h-[240px] overflow-y-auto pr-1" : ""}`}>
            {householdMembers.map((member) => (
              <div
                key={member.id}
                onClick={() => setSelectedLeadId(member.id)}
                role="button"
                tabIndex={0}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  selectedLeadId === member.id
                    ? "border-primary bg-primary/10"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    setSelectedLeadId(member.id);
                  }
                }}
              >
                <div className="flex items-center gap-3">
                  <User className="h-8 w-8 text-gray-400" />
                  <div>
                    <h4 className="font-medium text-gray-900">
                      {member.name || "Unnamed Member"}
                    </h4>
                    <p className="text-sm text-gray-500">
                      {member.relationship || "Household Member"}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
            <div className="text-center">
              <User className="mx-auto h-8 w-8 text-gray-400 mb-2" />
              <p className="text-sm text-gray-500">
                No Household Members Found
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Click the button above to add household members
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Select Policy Type
        </h3>
        <p className="text-sm text-gray-600 mb-6">
          Choose the type of insurance policy you want to create
        </p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            setFormData((prev: any) => ({
              ...prev,
              productType: "Whole Life Insurance",
            }));
          }}
          className={`p-6 border-2 rounded-lg text-center transition-colors cursor-pointer ${
            formData.productType === "Whole Life Insurance"
              ? "border-primary bg-primary/10 ring-2 ring-primary"
              : "border-gray-200 hover:border-gray-300"
          }`}
        >
          <Shield className="h-8 w-8 mx-auto mb-3 text-primary" />
          <h4 className="font-medium text-gray-900 mb-1">Whole Life</h4>
          <p className="text-xs text-gray-500">
            Lifetime coverage with cash value
          </p>
        </button>

        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            setFormData((prev: any) => ({
              ...prev,
              productType: "Term Life Insurance",
            }));
          }}
          className={`p-6 border-2 rounded-lg text-center transition-colors cursor-pointer ${
            formData.productType === "Term Life Insurance"
              ? "border-primary bg-primary/10 ring-2 ring-primary"
              : "border-gray-200 hover:border-gray-300"
          }`}
        >
          <Heart className="h-8 w-8 mx-auto mb-3 text-red-500" />
          <h4 className="font-medium text-gray-900 mb-1">Term Life</h4>
          <p className="text-xs text-gray-500">Coverage for a specific term</p>
        </button>

        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            setFormData((prev: any) => ({ ...prev, productType: "IUL" }));
          }}
          className={`p-6 border-2 rounded-lg text-center transition-colors cursor-pointer ${
            formData.productType === "IUL"
              ? "border-primary bg-primary/10 ring-2 ring-primary"
              : "border-gray-200 hover:border-gray-300"
          }`}
        >
          <TrendingUp className="h-8 w-8 mx-auto mb-3 text-green-600" />
          <h4 className="font-medium text-gray-900 mb-1">IUL</h4>
          <p className="text-xs text-gray-500">Indexed Universal Life</p>
        </button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Policy Details
        </h3>
        <p className="text-sm text-gray-600 mb-6">
          Enter the details for this {formData.productType} policy
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-sm font-medium text-gray-700">
            Insurance Carrier
          </Label>
          {isNativePlatform() ? (
            <div className="relative">
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={carrierOpen}
                className="w-full justify-between font-normal"
                onClick={() => { setCarrierOpen(!carrierOpen); setCarrierSearch(""); }}
                type="button"
              >
                <span className="min-w-0 truncate text-left">
                  {formData.carrierName || (isCarriersLoading ? "Loading carriers..." : "Select carrier")}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
              {carrierOpen && (
                /* absolute OVERLAY — floats over the fields below instead of
                   pushing them down; anchored by layout (relative parent), so
                   it can't drift like viewport-positioned poppers here */
                <div className="absolute left-0 top-full z-30 mt-1.5 w-[calc(200%+1rem)] overflow-hidden rounded-md border bg-white shadow-xl">
                  <Input
                    placeholder="Search carriers..."
                    value={carrierSearch}
                    onChange={(e) => setCarrierSearch(e.target.value)}
                    className="rounded-none border-0 border-b focus-visible:ring-0"
                  />
                  <div className="max-h-[200px] overflow-y-auto overscroll-contain">
                    {agencyCarriers
                      .filter((c: any) => !carrierSearch || (c.carrierName || "").toLowerCase().includes(carrierSearch.toLowerCase()))
                      .map((carrier: any, index: number) => (
                        <button
                          key={carrier.id || `carrier-${index}`}
                          type="button"
                          onClick={() => {
                            setFormData((prev: any) => ({ ...prev, carrierName: carrier.carrierName }));
                            setCarrierOpen(false);
                          }}
                          className="flex w-full items-center px-3 py-2.5 text-left text-sm active:bg-gray-100"
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4 shrink-0",
                              formData.carrierName === carrier.carrierName ? "opacity-100" : "opacity-0"
                            )}
                          />
                          <span className="min-w-0 truncate">{carrier.carrierName}</span>
                        </button>
                      ))}
                    {agencyCarriers.filter((c: any) => !carrierSearch || (c.carrierName || "").toLowerCase().includes(carrierSearch.toLowerCase())).length === 0 && (
                      <p className="px-3 py-3 text-center text-sm text-gray-500">No carriers found.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
          <Popover open={carrierOpen} onOpenChange={setCarrierOpen} modal>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={carrierOpen}
                className="w-full justify-between font-normal"
              >
                <span className="min-w-0 truncate text-left">
                  {formData.carrierName || (isCarriersLoading ? "Loading carriers..." : "Select carrier")}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[--radix-popover-trigger-width] p-0"
              align="start"
              side="bottom"
              onWheel={(e) => e.stopPropagation()}
            >
              <Command>
                <CommandInput placeholder="Search carriers..." />
                <CommandList className="max-h-[200px]">
                  <CommandEmpty>No carriers found.</CommandEmpty>
                  <CommandGroup>
                    {agencyCarriers.map((carrier: any, index: number) => (
                      <CommandItem
                        key={carrier.id || `carrier-${index}`}
                        value={carrier.carrierName}
                        onSelect={() => {
                          setFormData((prev: any) => ({ ...prev, carrierName: carrier.carrierName }));
                          setCarrierOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            formData.carrierName === carrier.carrierName ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {carrier.carrierName}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          )}
        </div>

        <div className="space-y-2">
          <Label className="text-sm font-medium text-gray-700">
            Policy Number (Optional)
          </Label>
          <Input
            onChange={(e) =>
              setFormData((prev: any) => ({
                ...prev,
                policyNumber: e.target.value,
              }))
            }
            placeholder="POL-123456"
            value={formData.policyNumber}
          />
        </div>

        <div className="space-y-2">
          <Label className="text-sm font-medium text-gray-700">
            Annual Premium ($)
          </Label>
          <CurrencyInput
            id="annualPremium"
            value={formData.annualPremium}
            onChange={(value) =>
              setFormData((prev: any) => ({
                ...prev,
                annualPremium: value,
              }))
            }
            placeholder="xx"
            showIcon={true}
            containerClass=""
          />
        </div>

        <div className="space-y-2">
          <Label className="text-sm font-medium text-gray-700">
            Coverage Amount ($)
          </Label>
          <CurrencyInput
            id="coverageAmount"
            value={formData.coverageAmount}
            onChange={(value) =>
              setFormData((prev: any) => ({
                ...prev,
                coverageAmount: value,
              }))
            }
            placeholder="xx"
            showIcon={true}
            containerClass=""
          />
        </div>

        <div className="space-y-2">
          <Label className="text-sm font-medium text-gray-700">
            Effective Date
          </Label>
          <Popover>
            <div className="relative">
              <Input
                type="date"
                value={formData.issuedAt ?? ""}
                onChange={(e) =>
                  setFormData((prev: any) => ({
                    ...prev,
                    issuedAt: e.target.value,
                  }))
                }
                className="w-full pr-10"
              />
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                >
                  <CalendarIcon className="h-4 w-4 text-gray-400" />
                </Button>
              </PopoverTrigger>
            </div>
            <PopoverContent align="start" className="w-auto p-0">
              <Calendar
                mode="single"
                selected={formData.issuedAt ? new Date(formData.issuedAt + 'T12:00:00') : undefined}
                onSelect={(date) => {
                  if (date) {
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    setFormData((prev: any) => ({
                      ...prev,
                      issuedAt: `${year}-${month}-${day}`
                    }));
                  }
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Hide the policy dialog while Add Household Member is up — stacking
          both reads fine on a wide desktop but is a sandwich on a phone. Form
          state lives in this component, so it survives the swap. */}
      <Dialog onOpenChange={onOpenChange} open={isOpen && !isHouseholdModalOpen}>
        <ResponsiveDialogContent size="2xl" className="p-0 border-0 shadow-lg">
          <DialogHeader className="p-6 pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className="h-6 w-6 text-primary" />
                <DialogTitle className="text-lg font-semibold text-gray-900">
                  Create New Policy
                </DialogTitle>
              </div>
            </div>
          </DialogHeader>

          <div className="px-6 pb-4">
            {renderStepIndicator()}

            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
          </div>

          {/* Bottom Navigation */}
          <div className="px-6 pb-6 pt-4 border-t border-gray-200">
            <div className="flex justify-between">
              <Button
                className="flex items-center gap-2"
                disabled={currentStep === 1}
                onClick={handleBack}
                variant="outline"
              >
                <ChevronLeft className="h-4 w-4" />
                Back
              </Button>

              {currentStep < 3 ? (
                <Button
                  className="flex items-center gap-2 bg-primary hover:bg-primary/90"
                  onClick={handleNext}
                  disabled={
                    (currentStep === 1 && !selectedLeadId) ||
                    (currentStep === 2 && !formData.productType)
                  }
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              ) : (
                <Button
                  className="bg-primary hover:bg-primary/90"
                  onClick={handleSave}
                  disabled={
                    isSubmitting ||
                    !formData.carrierName.trim() ||
                    !formData.annualPremium.trim()
                  }
                >
                  {isSubmitting ? "Creating..." : "Create Policy"}
                </Button>
              )}
            </div>
          </div>
        </ResponsiveDialogContent>
      </Dialog>

      {/* Household Member Modal */}
      <CreateHouseholdMemberModal
        isOpen={isHouseholdModalOpen}
        onOpenChange={setIsHouseholdModalOpen}
        leadId={leadId}
        leadName={leadName}
        householdId={householdId}
        onSuccess={async () => {
          // Only refetch to avoid flicker - keep cached data during transition
          await queryClient.refetchQueries({
            queryKey: ["households"],
          });
        }}
      />
    </>
  );
};

export default CreateLeadPolicy;
